/**
 * Team → Reviews → one document.
 *
 * The review viewer: the employee-filed markdown rendered full-width, every
 * block clickable to anchor a comment to its source lines (GitHub-review
 * model — comments persist server-side as drafts, invisible to the employee,
 * until one Finish action sends them all and enqueues the feedback job into
 * the employee's queue). Git stays canonical for the document itself; what
 * is reviewed here is the copy the employee pushed at drop-off.
 *
 * Long-document ergonomics live in three pieces:
 * - a sticky toolbar (`ReviewToolbar`) keeps the finish actions, version
 *   switcher, diff toggle and fold controls reachable mid-scroll;
 * - the body folds into sections (H2, fallback H1) with a smart default —
 *   sections carrying comments or changes since the previous version start
 *   open, the rest collapsed. Folding hides via CSS, never unmounts, so a
 *   half-written composer survives a collapse;
 * - version snapshots and a structured diff (`mode: current | version |
 *   diff`) make resubmits reviewable against what was commented on.
 *   Commenting exists only in `current` mode — old versions and diffs are
 *   read-only.
 *
 * Finish is a triage step, not a destination: it sends the comments and
 * moves straight on to the next pending review of the same kind and queue
 * (the inbox when the queue is empty), announcing the outcome in a toast.
 * Snooze advances the same way (`lib/reviewQueue.ts :: nextInQueue`).
 * The finished document stays read-only; feedback draft actions disappear.
 * Published Conversation & updates stays interactive at every review status,
 * independent of the feedback decision and document version navigation.
 *
 * A meeting briefing (kind `briefing`) is never approved: before the meeting
 * the owner comments and sends the comments; the meeting ends it – the
 * close panel (`BriefingClose`) takes a debrief or none. No snooze. Opening
 * a briefing marks its current version read.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  adminApi,
  type AdminReviewDoc,
  type EmailDraftMeta,
} from "../../api/admin";
import { apiErrorDetail } from "../../api/client";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { PriorityBadge } from "../../components/PriorityBadge";
import { QueryBoundary } from "../../components/QueryBoundary";
import { ShortId } from "../../components/ShortId";
import {
  ReviewMarkdown,
  type BlockRange,
  type ReviewFolding,
} from "../../components/ReviewMarkdown";
import { BriefingClose } from "../../components/review/BriefingClose";
import { EmailAttachments } from "../../components/review/EmailAttachments";
import { EmailReviewContext } from "../../components/review/EmailReviewContext";
import { EmailTranslation } from "../../components/review/EmailTranslation";
import { EmailSignature } from "../../components/review/EmailSignature";
import { EmailDraftPriority, ReviewTriage } from "../../components/review/ReviewTriage";
import {
  directSendMailbox, draftCampaign, draftReviewSearch, emailOutcome, matchesDraftFilters,
  signatureBlock,
} from "../../lib/emailDrafts";
import { bundleUrl } from "../../lib/console";
import {
  briefingPhaseText, briefingStatus, closeOutcome, closeQuestion, isUnseen, meetingLabel, nextBriefing,
  type CloseContext,
} from "../../lib/briefings";
import {
  finishOutcome,
  finishQuestion,
  type EmailDelivery,
  type FinishContext,
  type FinishDecision,
} from "../../lib/reviewFinish";
import { submissionLine, whenText } from "../../lib/reviewVersions";
import { ReviewComments } from "../../components/review/ReviewComments";
import { ReviewConversation } from "../../components/review/ReviewConversation";
import { ReviewDiff } from "../../components/review/ReviewDiff";
import {
  ReviewToolbar,
  type ReviewViewMode,
} from "../../components/review/ReviewToolbar";
import { useConfirm } from "../../components/confirm/context";
import { SnoozeControl } from "../../components/review/SnoozeMenu";
import { useToast } from "../../components/toast/context";
import { ShortcutHelp } from "../../components/ui/ShortcutHelp";
import { useKeyboardShortcuts } from "../../lib/hooks/useKeyboardShortcuts";
import { nextInQueue } from "../../lib/reviewQueue";
import { formatReturnDate, snoozeLabel, snoozedUntilDate } from "../../lib/snooze";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import {
  agentDrawerUrl,
  reviewListLabel,
  reviewListUrl,
  reviewStatusTone,
  reviewUrl,
} from "../../lib/aiTeam";
import {
  changedNewLines,
  commentCountBySection,
  defaultCollapsedIds,
  parseSections,
  sectionForLine,
} from "../../lib/reviewSections";

export function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const reviewQuery = adminApi.aiTeam.review(id);
  const location = useLocation();
  // Each kind has its own inbox; a link under another one lands there.
  const kind = reviewQuery.data?.kind ?? "doc";
  if (reviewQuery.data && !location.pathname.startsWith(`${reviewListUrl(kind)}/`)) {
    return <Navigate to={`${reviewUrl(reviewQuery.data.id, kind)}${location.search}`} replace state={location.state} />;
  }

  return (
    <QueryBoundary
      query={reviewQuery}
      empty={
        <div className="empty-state">
          <div className="title">Review not found</div>
          <p className="hint">
            It may have been withdrawn.{" "}
            <Link className="link" to="/team/reviews">
              Back to AI Reviews
            </Link>
          </p>
        </div>
      }
      isEmpty={(doc) => !doc}
    >
      {(doc) => <ReviewDetail doc={doc} />}
    </QueryBoundary>
  );
}

function ReviewDetail({ doc }: { doc: AdminReviewDoc }) {
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const addComment = adminApi.aiTeam.addReviewComment();
  const updateComment = adminApi.aiTeam.updateReviewComment();
  const deleteComment = adminApi.aiTeam.deleteReviewComment();
  const finish = adminApi.aiTeam.finishReview();
  const askQuestion = adminApi.aiTeam.askReviewQuestion();

  const [selection, setSelection] = useState<BlockRange | null>(null);
  const [mode, setMode] = useState<ReviewViewMode>("current");
  const [viewVersion, setViewVersion] = useState<number | null>(null);
  const [collapsedOverride, setCollapsedOverride] =
    useState<Set<string> | null>(null);
  const [pendingScrollLine, setPendingScrollLine] = useState<number | null>(
    null,
  );
  const docPanelRef = useRef<HTMLElement | null>(null);

  // A resubmit landing mid-review resets the view to the fresh current
  // version (render-time prev-key pattern — no effect, no extra paint).
  const versionKey = `${doc.id}:${doc.content_version}`;
  const [seenKey, setSeenKey] = useState(versionKey);
  if (seenKey !== versionKey) {
    setSeenKey(versionKey);
    setCollapsedOverride(null);
    setMode("current");
    setViewVersion(null);
    setSelection(null);
  }

  // Advancing to the next review keeps this component mounted (same route,
  // new :id), so the scroll position would survive into a different
  // document — mid-page, below the toolbar. Reset it per document.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [doc.id]);

  const feedback = useDraft(draftKey("review-feedback", doc.id, doc.content_version));
  const isEmail = (doc.kind ?? "doc") === "email";
  const isBriefing = doc.kind === "briefing";
  // A held briefing takes no more comments – only its close.
  const held = isBriefing && doc.phase === "held";
  const reviewing = doc.status === "pending" && !held;
  const briefingOpen = isBriefing && (doc.status === "pending" || doc.status === "changes_requested");
  const sendMailboxes = adminApi.aiTeam.directSendMailboxes();
  const approveBlocked = isEmail ? signatureBlock(doc.email_meta) : null;
  // The mailbox the server sends from on "Approve & send" – one of the
  // owner's Exchange mailboxes; attachments go out with it.
  const sendFrom = isEmail ? directSendMailbox(doc.email_meta, sendMailboxes.data) : null;
  const queueSearch = isEmail ? draftReviewSearch(doc, location.search) : "";
  const campaign = isEmail ? draftCampaign(doc) : null;
  const inboxUrl = `${reviewListUrl(doc.kind)}${queueSearch}`;
  const comments = useMemo(() => doc.comments ?? [], [doc.comments]);
  const drafts = comments.filter((c) => c.state === "draft");

  // The inbox list of this kind — shares its key with the inbox page (the
  // email inbox and the briefings show every open round), so it is normally
  // already cached. It only drives "what comes after this one" on finish;
  // the list is read as it stood at click time, before the finish
  // invalidates the subtree.
  const queueQuery = adminApi.aiTeam.reviews(
    isEmail || isBriefing ? "open" : "pending",
    isEmail ? "email" : isBriefing ? "briefing" : "doc",
  );
  // Email drafts advance within the topic and priority the inbox was showing.
  const inQueueContext = isEmail
    ? (d: AdminReviewDoc) => matchesDraftFilters(d, new URLSearchParams(queueSearch))
    : undefined;

  // Triage moves forward: the next review of the same queue, or the inbox
  // once it is empty. Staying put would re-show the document that was just
  // handed back, which reads as "nothing happened".
  const advance = (outcome: string, next: AdminReviewDoc | undefined, error = false) => {
    const message = next
      ? `${outcome} · next: ${next.email_meta?.subject || next.title}`
      : `${outcome} · queue empty`;
    if (error) toast.error(message);
    else toast.ok(message);
    navigate(next ? `${reviewUrl(next.id, next.kind)}${queueSearch}` : inboxUrl);
  };

  const snooze = adminApi.aiTeam.snoozeReview();
  const wake = adminApi.aiTeam.wakeReview();
  const closeBriefing = adminApi.aiTeam.closeBriefing();
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [nowMs] = useState(() => Date.now());

  // Opening a briefing reads its current version – once per version and
  // mount, so a failed call does not repeat on every poll.
  const { mutate: markSeen } = adminApi.aiTeam.markReviewSeen();
  const seenSent = useRef<string | null>(null);
  const unseen = isBriefing && isUnseen(doc);
  useEffect(() => {
    if (!unseen || seenSent.current === versionKey) return;
    seenSent.current = versionKey;
    markSeen({ reviewId: doc.id, version: doc.content_version });
  }, [unseen, versionKey, markSeen, doc.id, doc.content_version]);

  // Versions + diff. The vN-1 → vN diff is fetched eagerly whenever history
  // exists — it drives the changed-block markers and the smart fold default,
  // not just the diff view. All of it degrades gracefully while the backend
  // lacks the endpoints (the queries just error; the toolbar hides history).
  const versionsQuery = adminApi.aiTeam.reviewVersions(doc.id);
  const versions = versionsQuery.data;
  const eagerFrom = doc.content_version > 1 ? doc.content_version - 1 : null;
  const eagerDiff = adminApi.aiTeam.reviewDiff(
    doc.id,
    eagerFrom,
    doc.content_version,
  );
  const diffFrom = viewVersion ?? eagerFrom;
  const diffView = adminApi.aiTeam.reviewDiff(
    doc.id,
    mode === "diff" ? diffFrom : null,
    doc.content_version,
  );
  const versionQuery = adminApi.aiTeam.reviewVersion(
    doc.id,
    mode === "version" ? viewVersion : null,
  );

  // Sections + fold state. `collapsedOverride === null` means "live smart
  // default" — late-arriving diff data or a colleague's new comment can pop
  // a section open until the first manual touch snapshots ownership.
  const sections = useMemo(
    () => parseSections(doc.content_md ?? ""),
    [doc.content_md],
  );
  const changedLines = useMemo(
    () => (eagerDiff.data ? changedNewLines(eagerDiff.data) : null),
    [eagerDiff.data],
  );
  // Stale anchors point at replaced text — only current-version comments
  // mark blocks, count into section badges, and hold sections open.
  const currentCommentLines = useMemo(() => {
    const lines = new Set<number>();
    for (const c of comments) {
      const stale = c.stale ?? c.content_version < doc.content_version;
      if (!stale) lines.add(c.line_start);
    }
    return lines;
  }, [comments, doc.content_version]);
  const commentCounts = useMemo(
    () => commentCountBySection(sections, comments, doc.content_version),
    [sections, comments, doc.content_version],
  );
  const collapsed = useMemo(
    () =>
      collapsedOverride ??
      defaultCollapsedIds(sections, currentCommentLines, changedLines),
    [collapsedOverride, sections, currentCommentLines, changedLines],
  );

  const onToggle = useCallback(
    (id: string) => {
      const next = new Set(collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setCollapsedOverride(next);
    },
    [collapsed],
  );

  const folding: ReviewFolding | undefined = useMemo(
    () =>
      sections.length > 1
        ? {
            sections,
            collapsedIds: collapsed,
            onToggle,
            commentCounts,
            changedLines,
          }
        : undefined,
    [sections, collapsed, onToggle, commentCounts, changedLines],
  );

  // Comment → block navigation. The expand happens first; the effect runs
  // after that commit, when the target is display:block again.
  const jumpToLine = useCallback(
    (line: number) => {
      if (mode !== "current") {
        setMode("current");
        setViewVersion(null);
      }
      const s = sectionForLine(sections, line);
      if (s && collapsed.has(s.id)) {
        const next = new Set(collapsed);
        next.delete(s.id);
        setCollapsedOverride(next);
      }
      setPendingScrollLine(line);
    },
    [mode, sections, collapsed],
  );

  useEffect(() => {
    if (pendingScrollLine == null) return;
    const root: ParentNode = docPanelRef.current ?? document;
    const blocks = root.querySelectorAll<HTMLElement>(
      ".review-block[data-line-start]",
    );
    // Innermost match: the smallest range containing the line.
    let found: HTMLElement | null = null;
    let bestSpan = Infinity;
    for (const el of Array.from(blocks)) {
      const start = Number(el.dataset.lineStart);
      const end = Number(el.dataset.lineEnd ?? start);
      const span = end - start;
      if (start <= pendingScrollLine && pendingScrollLine <= end && span < bestSpan) {
        found = el;
        bestSpan = span;
      }
    }
    if (found) {
      const el = found;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("review-block--flash");
      window.setTimeout(() => el.classList.remove("review-block--flash"), 1300);
    }
    setPendingScrollLine(null);
  }, [pendingScrollLine]);

  // Async so the composer can clear its persisted draft on success only — a
  // failed add must leave the typed comment in the box.
  const submitDraft = async (text: string) => {
    if (!selection || !text.trim()) return;
    try {
      await addComment.mutateAsync({
        reviewId: doc.id,
        payload: {
          line_start: selection.line_start,
          line_end: selection.line_end,
          quoted_text: selection.quoted_text,
          body_md: text,
        },
      });
      setSelection(null);
      toast.ok("Draft comment added");
    } catch (e) {
      toast.error(e);
      throw e;
    }
  };

  const onFinish = async (decision: FinishDecision, delivery?: EmailDelivery) => {
    const ctx: FinishContext = {
      author: doc.agent_slug,
      isEmail,
      isBriefing,
      feedbackCount: drafts.length + (feedback.draft.trim() ? 1 : 0),
      mailbox: sendFrom ?? doc.email_meta?.mailbox_address,
      attachments: doc.email_meta?.attachments ?? [],
      signature: doc.signature,
    };
    if (!(await confirm(finishQuestion(decision, delivery, ctx)))) return;
    const next = isBriefing
      ? nextBriefing(queueQuery.data ?? [], doc)
      : nextInQueue(queueQuery.data ?? [], doc, inQueueContext);
    finish.mutate(
      { reviewId: doc.id, decision, feedback_md: feedback.draft, delivery },
      {
        onSuccess: (r) => {
          feedback.clearDraft();
          const outcome = finishOutcome(r, ctx);
          advance(outcome.text, next, outcome.error);
        },
        onError: (e) => toast.error(e),
      },
    );
  };

  const onSnooze = (days: number) => {
    // Snapshot the queue before the snooze invalidates it, like Finish.
    const next = nextInQueue(queueQuery.data ?? [], doc, inQueueContext);
    snooze.mutate(
      { reviewId: doc.id, days },
      {
        onSuccess: (r) =>
          advance(
            r.snoozed_until
              ? `Snoozed until ${formatReturnDate(snoozedUntilDate(r.snoozed_until), Date.now())}`
              : "Snoozed",
            next,
          ),
        onError: (e) => toast.error(e),
      },
    );
  };

  // Closing a briefing advances like a finish: to the next briefing that
  // waits on the owner, or back to the list.
  const onClose = async (notes: string): Promise<boolean> => {
    const ctx: CloseContext = { author: doc.agent_slug, held, hasNotes: !!notes.trim() };
    if (!(await confirm(closeQuestion(ctx)))) return false;
    const next = nextBriefing(queueQuery.data ?? [], doc);
    try {
      await closeBriefing.mutateAsync({ reviewId: doc.id, notesMd: notes });
    } catch (e) {
      toast.error(e);
      throw e;
    }
    advance(closeOutcome(ctx), next);
    return true;
  };

  // `a` approves the safe way: an email becomes a draft, never a send.
  // A briefing has no approve and no snooze.
  const canQuickApprove =
    reviewing && !isBriefing && mode === "current" && !selection && !finish.isPending && !snooze.isPending
    && !approveBlocked;

  useKeyboardShortcuts(
    {
      s: (e) => {
        if (!e.repeat && reviewing && !isBriefing) setSnoozeOpen(true);
      },
      a: (e) => {
        if (!e.repeat && canQuickApprove) void onFinish("approve", isEmail ? "draft" : undefined);
      },
      "?": () => setHelpOpen((v) => !v),
    },
    { enabled: !snoozeOpen },
  );

  // Keyed on the anchor: selecting a different block remounts the composer
  // with an empty draft, so no useEffect is needed to reset it — and while
  // the anchor is unchanged the element reconciles, keeping the typed text.
  const composer =
    reviewing && selection ? (
      <CommentComposer
        key={selection.key}
        reviewId={doc.id}
        range={selection}
        pending={addComment.isPending}
        onSubmit={submitDraft}
        onCancel={() => setSelection(null)}
      />
    ) : null;

  const snapshot = versionQuery.data;
  // The envelope follows the view mode: an old snapshot shows ITS
  // recipients/subject/files, and the diff view surfaces envelope changes the
  // body diff cannot (an envelope-only resubmit produces an identical body diff).
  const envelopeMeta =
    mode === "version"
      ? (snapshot?.email_meta ?? doc.email_meta ?? null)
      : (doc.email_meta ?? null);
  const envelopeCompare =
    mode === "diff"
      ? (versions?.find((v) => v.content_version === diffFrom)?.email_meta ?? null)
      : undefined;

  return (
    <>
      <PageHeader
        title={doc.title}
        description={
          isEmail
            ? `to: ${doc.email_meta?.to?.join(", ") || "—"}`
            : isBriefing
              ? [meetingLabel(doc, nowMs), briefingPhaseText(doc, nowMs)].filter(Boolean).join(" · ")
              : doc.path
        }
        actions={
          <Link className="btn btn-sm btn-ghost" to={inboxUrl}>
            ← {reviewListLabel(doc.kind)}
          </Link>
        }
      />

      <div className="flex items-center gap-2 mb-3 flex-wrap text-sm">
        {isEmail ? <>
          {campaign && doc.bundle ? (
            <Link className="link-quiet text-xs" to={bundleUrl(campaign.slug, doc.bundle.id)} title="Open the bundle in the campaign">
              {campaign.name} · {doc.bundle.title} →
            </Link>
          ) : null}
          <span className="text-xs text-ink-low">{doc.topic?.name ?? "Uncategorized"}</span>
          <EmailDraftPriority priority={doc.priority} />
          <ReviewTriage key={doc.id} doc={doc} />
        </> : <>
          {doc.priority != null ? <PriorityBadge priority={doc.priority} /> : null}
          <span className="text-xs text-ink-low">{doc.topic?.name ?? "Uncategorized"}</span>
          <ReviewTriage key={doc.id} doc={doc} />
        </>}
        {isBriefing ? (
          <Pill value={briefingStatus(doc).label} tone={briefingStatus(doc).tone} />
        ) : (
          <Pill value={emailOutcome(doc) ?? doc.status} tone={reviewStatusTone(doc.status)} />
        )}
        {doc.queue === "returned" ? <Pill value="back from snooze" tone="signal" /> : null}
        {doc.agent_slug && doc.agent_id ? (
          <Link
            className="font-mono text-xs text-ink-low link-quiet"
            to={agentDrawerUrl(doc.agent_id)}
          >
            {doc.agent_slug}
          </Link>
        ) : null}
        <span className="text-xs text-ink-low tnum">
          <ShortId id={doc.id} /> · {submissionLine(doc)}
          {doc.filed_on ? ` · filed ${doc.filed_on.slice(0, 10)}` : ""}
          {doc.baseline_commit ? ` · ${doc.baseline_commit.slice(0, 8)}` : ""}
        </span>
      </div>

      <ShortcutHelp
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        shortcuts={[
          ...(reviewing && !isBriefing ? [{ keys: ["s"], label: "Snooze" }] : []),
          ...(reviewing && !isBriefing && !approveBlocked
            ? [{ keys: ["a"], label: isEmail ? "Approve & draft" : "Approve" }]
            : []),
          { keys: ["Esc"], label: "Close a menu or dialog" },
          { keys: ["?"], label: "Show shortcuts" },
        ]}
      />

      {doc.queue === "snoozed" && doc.snoozed_until ? (
        <div role="status" className="callout callout--info items-center mb-3">
          <span className="flex-1">
            Snoozed until {formatReturnDate(snoozedUntilDate(doc.snoozed_until), nowMs)}
            {doc.snooze_days ? ` (${snoozeLabel(doc.snooze_days)}` : ""}
            {doc.snooze_days && (doc.snooze_count ?? 0) > 1 ? `, snoozed ${doc.snooze_count}×)` : doc.snooze_days ? ")" : ""}
            {" "}– it comes back on its own.
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={wake.isPending}
            onClick={() =>
              wake.mutate(
                { reviewId: doc.id },
                { onSuccess: () => toast.ok("Back from snooze"), onError: (e) => toast.error(e) },
              )
            }
          >
            Wake now
          </button>
        </div>
      ) : null}

      <ReviewToolbar
        snooze={
          isBriefing ? undefined : (
            <SnoozeControl
              doc={doc}
              onPick={onSnooze}
              disabled={snooze.isPending || finish.isPending}
              open={snoozeOpen}
              onOpenChange={setSnoozeOpen}
            />
          )
        }
        versions={versions}
        currentVersion={doc.content_version}
        mode={mode}
        viewVersion={viewVersion}
        onVersionSelect={(v) => {
          if (v === null) {
            setMode("current");
            setViewVersion(null);
          } else {
            setViewVersion(v);
            setMode("version");
          }
        }}
        onDiffToggle={() =>
          setMode(
            mode === "diff" ? (viewVersion != null ? "version" : "current") : "diff",
          )
        }
        hasSections={!!folding}
        onExpandAll={() => setCollapsedOverride(new Set())}
        onCollapseAll={() =>
          setCollapsedOverride(new Set(sections.map((s) => s.id)))
        }
        reviewing={reviewing}
        feedbackCount={drafts.length + (feedback.draft.trim() ? 1 : 0)}
        isEmail={isEmail}
        isBriefing={isBriefing}
        canSend={!!sendFrom}
        approveBlocked={approveBlocked}
        finishPending={finish.isPending}
        onFinish={(decision, delivery) => void onFinish(decision, delivery)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        <section className="panel lg:col-span-2" ref={docPanelRef}>
          {isEmail ? (
            <>
              <EmailEnvelope meta={envelopeMeta} compareMeta={envelopeCompare} />
              <EmailAttachments meta={envelopeMeta} compareMeta={envelopeCompare} />
            </>
          ) : null}
          {mode === "current" ? (
            <>
              {reviewing ? (
                <p className="text-xs text-ink-low mb-3">
                  Click any block to anchor a comment to its lines
                  {folding ? " · headings fold their section" : ""}.
                </p>
              ) : null}
              <ReviewMarkdown
                contentMd={doc.content_md ?? ""}
                onSelect={reviewing ? setSelection : undefined}
                activeKey={selection?.key ?? null}
                activeExtra={composer}
                commentedLines={currentCommentLines}
                folding={folding}
              />
            </>
          ) : mode === "version" ? (
            <>
              <div className="review-view-banner">
                Viewing v{viewVersion}
                {snapshot?.submitted_at ? ` – submitted ${whenText(snapshot.submitted_at)}` : ""}
                . Read-only; comments anchor to the current version.{" "}
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    setMode("current");
                    setViewVersion(null);
                  }}
                >
                  Back to v{doc.content_version} (current)
                </button>
              </div>
              {versionQuery.isError ? (
                <p className="text-sm text-ink-low">
                  {apiErrorDetail(versionQuery.error)}
                </p>
              ) : snapshot?.content_md != null ? (
                <ReviewMarkdown contentMd={snapshot.content_md} />
              ) : (
                <p className="text-sm text-ink-low">Loading v{viewVersion}…</p>
              )}
            </>
          ) : (
            <>
              <div className="review-view-banner">
                Changes v{diffFrom} → v{doc.content_version}. Read-only;
                comments anchor to the current version.
              </div>
              {diffView.isError ? (
                <p className="text-sm text-ink-low">
                  {apiErrorDetail(diffView.error)}
                </p>
              ) : diffView.data ? (
                <ReviewDiff diff={diffView.data} />
              ) : (
                <p className="text-sm text-ink-low">Computing diff…</p>
              )}
            </>
          )}
          {isEmail ? <EmailSignature signature={doc.signature} /> : null}
          {isEmail ? <EmailTranslation meta={envelopeMeta} /> : null}
          {isEmail && <EmailReviewContext
            reviewId={doc.id}
            version={doc.content_version}
            isReply={!!doc.email_meta?.reply_to_message_id}
            replyTranslation={envelopeMeta?.reply_translation_md}
          />}
        </section>

        <aside className="panel sticky-under-topbar">
          {briefingOpen && held ? (
            <BriefingClose key={doc.id} reviewId={doc.id} held author={doc.agent_slug ?? "the employee"}
              pending={closeBriefing.isPending} onClose={onClose} />
          ) : null}
          <ReviewConversation key={doc.id} reviewId={doc.id} agentId={doc.agent_id}
            agentLabel={doc.agent_slug ?? "the employee"}
            version={mode === "version" ? viewVersion ?? doc.content_version : doc.content_version}
            entries={doc.entries ?? []} pending={askQuestion.isPending}
            onAsk={(payload) => askQuestion.mutateAsync({ reviewId: doc.id, payload })} />
          {isEmail && reviewing && (
            <div className="mb-5 space-y-3">
              <label htmlFor="general-feedback" className="font-heading font-semibold">General feedback</label>
              <p className="text-xs text-ink-low">Add context, CRM corrections or a reason to reject this email. Paste your voice transcription here.</p>
              <textarea id="general-feedback" className="input w-full" rows={7}
                value={feedback.draft} onChange={(e) => feedback.setDraft(e.target.value)}
                disabled={finish.isPending} placeholder="What should the agent know?" />
              <div className="flex gap-2 flex-wrap">
                <button className="btn btn-sm btn-main" disabled={finish.isPending || !feedback.draft.trim()} onClick={() => void onFinish("request_changes")}>Request changes</button>
                <button className="btn btn-sm btn-ghost" disabled={finish.isPending} onClick={() => void onFinish("reject")}>Reject email</button>
              </div>
              <p className="text-xs text-ink-low">Request changes sends your feedback for a revision you review again. Approve &amp; draft has the author work it in and create the mailbox draft right away. Reject ends this proposal; a reason is optional and will be remembered by the agent.</p>
            </div>
          )}
          <h2 className="font-heading font-semibold text-ink mb-2">
            Comments{" "}
            <span className="text-xs text-ink-low tnum">
              {comments.length || "none yet"}
            </span>
          </h2>
          {comments.length === 0 ? (
            <p className="text-sm text-ink-low">
              {reviewing
                ? "Click a block in the document to add the first one."
                : isBriefing
                  ? "No comments on this briefing."
                  : "This review was finished without comments."}
            </p>
          ) : (
            <ReviewComments
              comments={comments}
              docVersion={doc.content_version}
              reviewing={reviewing}
              onJump={jumpToLine}
              onEdit={(c, bodyMd) =>
                updateComment.mutate(
                  { reviewId: doc.id, commentId: c.id, bodyMd },
                  {
                    onSuccess: () => toast.ok("Comment updated"),
                    onError: (e) => toast.error(e),
                  },
                )
              }
              onDelete={(c) =>
                deleteComment.mutate(
                  { reviewId: doc.id, commentId: c.id },
                  {
                    onSuccess: () => toast.ok("Draft discarded"),
                    onError: (e) => toast.error(e),
                  },
                )
              }
            />
          )}
          {briefingOpen && !held ? (
            <BriefingClose key={doc.id} reviewId={doc.id} held={false} author={doc.agent_slug ?? "the employee"}
              pending={closeBriefing.isPending} onClose={onClose} />
          ) : null}
        </aside>
      </div>
    </>
  );
}

/**
 * Read-only envelope header for a kind='email' review — developer-console
 * key/value rows above the body. With `compareMeta` (diff mode) each changed
 * field shows the previous value in a warn-toned "was:" line; that comparison
 * is the only place an envelope-only resubmit is visible, since the body
 * diff comes back identical.
 */
function EmailEnvelope({
  meta,
  compareMeta,
}: {
  meta: EmailDraftMeta | null;
  compareMeta?: EmailDraftMeta | null;
}) {
  const rows: { label: string; key: keyof EmailDraftMeta }[] = [
    { label: "To", key: "to" },
    { label: "Cc", key: "cc" },
    { label: "Bcc", key: "bcc" },
    { label: "Subject", key: "subject" },
    { label: "Mailbox", key: "mailbox_address" },
    { label: "Reply to", key: "reply_to_message_id" },
    { label: "Format", key: "body_format" },
  ];
  const fmt = (v: EmailDraftMeta[keyof EmailDraftMeta]): string =>
    Array.isArray(v) ? v.join(", ") : v ? String(v) : "";

  return (
    <div className="mb-4 pb-3 border-b border-rule font-mono text-xs">
      {rows.map(({ label, key }) => {
        const value = fmt(meta?.[key] ?? null);
        const prev = compareMeta !== undefined ? fmt(compareMeta?.[key] ?? null) : null;
        const changed = compareMeta !== undefined && prev !== value;
        // Format defaults to plain — hide the row unless it says otherwise.
        if (!value && !changed) return null;
        if (key === "body_format" && value === "plain" && !changed) return null;
        return (
          <div key={key} className="flex gap-2 py-0.5">
            <span className="text-ink-low w-24 shrink-0">{label}</span>
            <span className="min-w-0 break-words">
              <span className="text-ink">{value || "—"}</span>
              {changed ? (
                <span className="block text-ink-low line-through">
                  was: {prev || "—"}
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The inline comment box, owning its own draft text.
 *
 * The draft deliberately does NOT live on the page: a keystroke must not
 * re-render the document (react-markdown re-parses the whole body, and any
 * churn there is felt in a long document). The parent only learns the text
 * on submit.
 *
 * It is also mirrored to ``localStorage`` per (review, anchor): the composer
 * unmounts for reasons the typist never caused — a failed poll, a reload, a
 * navigation — and a comment on a review is long-form text worth keeping.
 */
function CommentComposer({
  reviewId,
  range,
  pending,
  onSubmit,
  onCancel,
}: {
  reviewId: string;
  range: BlockRange;
  pending: boolean;
  onSubmit: (text: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { draft: text, setDraft: setText, clearDraft } = useDraft(
    draftKey("review", reviewId, `${range.line_start}-${range.line_end}`),
  );
  const submit = async () => {
    if (!text.trim() || pending) return;
    try {
      await onSubmit(text);
      clearDraft();
    } catch {
      // Reported by the page's toast; the draft stays for a retry.
    }
  };
  const lines =
    range.line_end !== range.line_start
      ? `L${range.line_start}–L${range.line_end}`
      : `L${range.line_start}`;

  return (
    <div
      className="review-composer"
      // Keep clicks inside the composer from re-selecting the block.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-xs text-ink-low mb-1 tnum">{lines}</div>
      <textarea
        className="input w-full"
        rows={3}
        autoFocus
        placeholder="Comment on this block…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) {
            void submit();
          }
        }}
      />
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          className="btn btn-sm btn-main"
          disabled={!text.trim() || pending}
          onClick={() => void submit()}
        >
          Add draft comment
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <span className="text-xs text-ink-low">⌘↵ to add · Esc to cancel</span>
      </div>
    </div>
  );
}
